import React, { useState, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';
import { trackBookSessionClick } from '../utils/analytics';

const FloatingBookButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling 400px
      if (window.scrollY > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    const openHandler = () => setModalOpen(true);
    const closeHandler = () => setModalOpen(false);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('open-travel-enquiry', openHandler);
    window.addEventListener('close-travel-enquiry', closeHandler);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('open-travel-enquiry', openHandler);
      window.removeEventListener('close-travel-enquiry', closeHandler);
    };
  }, []);

  const scrollToContact = () => {
    trackBookSessionClick('floating_button');
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!isVisible || modalOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '8px'
      }}
    >
      {/* Minimized state - just icon */}
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(13, 148, 136, 0.5)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            animation: 'pulse 2s infinite'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 25px rgba(13, 148, 136, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(13, 148, 136, 0.5)';
          }}
          data-testid="floating-book-btn-minimized"
        >
          <Calendar size={24} color="white" />
        </button>
      ) : (
        /* Expanded state */
        <div
          style={{
            background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
            borderRadius: '16px',
            padding: '16px 20px',
            boxShadow: '0 8px 32px rgba(13, 148, 136, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            maxWidth: '280px',
            animation: 'slideIn 0.3s ease'
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            data-testid="floating-book-btn-close"
          >
            <X size={14} color="white" />
          </button>

          {/* Content */}
          <div style={{ paddingRight: '20px' }}>
            <p style={{ 
              color: '#E5C575', 
              fontWeight: 700, 
              fontSize: '0.9rem',
              margin: 0,
              marginBottom: '4px'
            }}>
              Ready to Excel?
            </p>
            <p style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: '0.8rem',
              margin: 0,
              lineHeight: 1.4
            }}>
              Book your first session today and start your journey to top grades!
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={scrollToContact}
            style={{
              background: '#E5C575',
              color: '#0F172A',
              border: 'none',
              borderRadius: '9999px',
              padding: '12px 24px',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'transform 0.2s ease, background 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.background = '#FFC107';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = '#E5C575';
            }}
            data-testid="floating-book-btn-cta"
          >
            <Calendar size={18} />
            Book Now
          </button>

          {/* Free consultation link */}
          <a
            href="https://calendly.com/drmomakobia/studentcall"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: '0.75rem',
              textAlign: 'center',
              textDecoration: 'underline'
            }}
          >
            or schedule a free call first
          </a>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(13, 148, 136, 0.5); }
          50% { box-shadow: 0 4px 30px rgba(13, 148, 136, 0.8); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FloatingBookButton;
